import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Avatar, CentBasedInputCurrency, Icon, Tag, Tooltip } from 'components';
import { JobTitleSelect } from 'components/JobTitleSelect/JobTitleSelect';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { Appearance, lineHeight, theme, toRem } from 'design';
import { useDebounce } from 'hooks';
import { DisbursementMethod, EmployeeType, IPayee } from 'types';
import { centsToDollarsDisplay } from 'utils';
import { getPaymentExceedsLimitError } from 'utils/payout';
import {
  Bottom,
  CurrencyInputContainer,
  DesktopCurrencyInputContainer,
  DesktopInputContainer,
  Header,
  HeaderError,
  HeaderSection,
  IconButton,
  InputForm,
  Layout,
  NetLabel,
  NetLabelWrapper,
  NoteInput,
  NoteWrapper,
  StyledText,
  TopLabel,
  UserInfo,
  UserInfoLayout,
} from './styles';

const StyledJobTitleSelect = styled(JobTitleSelect)`
  max-width: ${toRem(200)};
  background: none;

  .react-dropdown-select {
    min-height: 0;
    height: ${toRem(22)};
    padding: 0 !important;
    border: none !important;
    background: none;
    text-transform: capitalize;
    color: ${theme.color.neutral[600]};

    @media (max-width: ${theme.breakpoint.small}px) {
      height: ${toRem(28)};
    }

    &:focus-within {
      background-color: transparent !important;
    }

    .react-dropdown-select-content {
      padding: 0;
      font-size: ${toRem(theme.font.size.xs)};
      line-height: ${lineHeight(theme.font.size.xs)};
    }

    .react-dropdown-select-input {
      background: none;
    }

    .drop-handle {
      padding: 0;
      background: none;
    }
  }

  .react-dropdown-select-dropdown {
    top: ${toRem(20)};
    left: ${toRem(-4)};
  }
`;

interface IPayeeInputItemProps {
  field?: string;
  hasCash?: boolean;
  hasNote?: boolean;
  removeDisabled?: boolean;
  onRemove: (index: number) => void;
  className?: string;
  isLoading?: boolean;
  payee: IPayee;
  index: number;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (field: string) => void;
  siteId: string;
  maxTipAmount?: number;
}

const PayeeInputItemComponent = ({ className, field = 'payees', hasCash, hasNote, index, isLoading, removeDisabled, onRemove, setFieldValue, setFieldTouched, siteId, maxTipAmount, payee, ...rest }: IPayeeInputItemProps) => {
  const { employee, tipAmount, cash, note } = payee;
  const isInstant = employee.disbursementMethod === DisbursementMethod.INSTANT;
  const userImage = employee.user?.avatarUrl;
  const hasPaymentMethod = !!employee.user?.card;
  const additionalJobTitles = employee.type === EmployeeType.ADMIN ? [{ title: DEFAULT_ORG_ADMIN_JOB_TITLE }] : [];
  const totalPaid = tipAmount + (hasCash ? cash : 0);

  const debouncedSetFieldTouched = useDebounce(setFieldTouched, 1000);

  const handleChange = (key: string, value: any) => {
    const fieldName = `${field}[${index}].${key}`;
    setFieldValue(fieldName, value, false);
    debouncedSetFieldTouched(fieldName);
  };

  const errorMessage = getPaymentExceedsLimitError(totalPaid, maxTipAmount);

  const renderPaymentInputs = (isDesktop: boolean) => {
    const CurrencyContainer = isDesktop ? DesktopCurrencyInputContainer : CurrencyInputContainer;
    return (
      <>
        {hasCash && (
          <CurrencyContainer>
            <TopLabel>{isLoading ? <Skeleton width={61} /> : 'Cash'}</TopLabel>
            <CentBasedInputCurrency tabIndex={index + 1} name={`${field}[${index}].cash`} value={cash?.toString()} onChange={(value: string) => handleChange('cash', parseInt(value, 10))} data-cy="cash-input" />
          </CurrencyContainer>
        )}

        <CurrencyContainer>
          <TopLabel>{isLoading ? <Skeleton width={61} /> : isInstant ? 'Instant' : 'Payroll'}</TopLabel>
          <CentBasedInputCurrency tabIndex={index + 1} name={`${field}[${index}].tipAmount`} value={tipAmount?.toString()} onChange={(value: string) => handleChange('tipAmount', parseInt(value, 10))} data-cy="tip-input" />
        </CurrencyContainer>

        <CurrencyContainer>
          <TopLabel>{isLoading ? <Skeleton width={61} /> : 'Total'}</TopLabel>
          <NetLabelWrapper>
            <NetLabel data-cy="net-amount">{centsToDollarsDisplay(totalPaid)}</NetLabel>
          </NetLabelWrapper>
        </CurrencyContainer>
      </>
    );
  };

  return (
    <Layout className={className} data-cy="payee-input-item" {...rest}>
      {errorMessage && <HeaderError data-cy="header-error">{errorMessage}</HeaderError>}
      <Header>
        <HeaderSection className="user-info">
          <UserInfoLayout>
            <Avatar data-cy="payee-avatar" id={payee.id} username={payee.name} image={userImage} hasPaymentMethod={hasPaymentMethod} isLoading={isLoading} />
            <UserInfo>
              <StyledText data-cy="payee-name">{isLoading ? <Skeleton width={77} /> : payee.name}</StyledText>
              <StyledJobTitleSelect
                siteId={siteId}
                onChange={(jobOption) => handleChange('employee.job', jobOption?.value)}
                placeholder="Select a job title"
                defaultValue={{ label: employee.job?.title, value: employee.job }}
                isLoading={isLoading}
                dataCy="payee-job-title-selector"
                additionalJobTitles={additionalJobTitles}
              />
              {isLoading ? <Skeleton width={50} /> : <Tag label={employee.disbursementMethod} appearance={Appearance.SECONDARY} dataCy="payee-disbursement-method" />}
            </UserInfo>
          </UserInfoLayout>
        </HeaderSection>

        <HeaderSection className="payment-input-section">
          <DesktopInputContainer>{renderPaymentInputs(true)}</DesktopInputContainer>
        </HeaderSection>
        <HeaderSection>
          <IconButton aria-label="Remove Payee" data-cy="remove-payee-btn" disabled={isLoading || removeDisabled} onClick={() => onRemove(index)}>
            {isLoading ? <Skeleton circle width={22} height={22} /> : <Icon icon="RemoveThick" width={11} height={11} tooltipId={`remove-disabled-${payee.id}`} />}
            {removeDisabled && <Tooltip id={`remove-disabled-${payee.id}`}>Must have at least one employee.</Tooltip>}
          </IconButton>
        </HeaderSection>
      </Header>

      <Bottom>
        <InputForm>{renderPaymentInputs(false)}</InputForm>
        {hasNote && (
          <NoteWrapper>
            <NoteInput
              type="text"
              tabIndex={index + 1}
              data-cy="note-input"
              maxLength={100}
              placeholder="Enter an optional note"
              defaultValue={note}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleChange('note', e.currentTarget.value)}
            />
          </NoteWrapper>
        )}
      </Bottom>
    </Layout>
  );
};

export const PayeeInputItem = React.memo(PayeeInputItemComponent);
